import { useEffect, useState } from "react";
import { RotateCw, ImagePlus, Bug } from "lucide-react";

// AnalyzerProgress — rotates through messages so the user can SEE work is happening.
// Pass a `previewImg` (data URL) to show a thumbnail. Pass a `steps` array to
// override the default messaging (e.g. nutrition: "Identifying ingredients…").
const DEFAULT_STEPS = [
  "Uploading your photo securely…",
  "Reading details — colors, proportions, structure…",
  "Compiling your personalised recommendations…",
];

export function AnalyzerProgress({
  previewImg,
  totalSeconds = 18,
  testId = "analyzer-progress",
  steps,
}) {
  const items = (Array.isArray(steps) && steps.length > 0) ? steps : DEFAULT_STEPS;
  const [step, setStep] = useState(0);
  useEffect(() => {
    const tick = Math.max(1500, (totalSeconds * 1000) / items.length);
    const id = setInterval(() => setStep((s) => Math.min(items.length - 1, s + 1)), tick);
    return () => clearInterval(id);
  }, [totalSeconds, items.length]);

  const pct = Math.min(95, ((step + 1) / items.length) * 100);
  return (
    <div className="photo-empty" data-testid={testId} style={{ marginTop: 8 }}>
      {previewImg && (
        <img
          src={previewImg}
          alt=""
          style={{ width: 120, height: 120, borderRadius: 18, objectFit: "cover", marginBottom: 14 }}
        />
      )}
      <div className="spinner" style={{ marginBottom: 12 }} />
      <div className="body" data-testid={`${testId}-step`} style={{ color: "var(--text)" }}>{items[step]}</div>
      <div
        style={{
          marginTop: 14,
          width: "100%",
          maxWidth: 240,
          height: 4,
          background: "var(--bg-elev-2)",
          borderRadius: 4,
          overflow: "hidden",
        }}
        data-testid={`${testId}-bar`}
      >
        <div
          style={{
            width: `${pct}%`,
            height: "100%",
            background: "var(--gold)",
            transition: "width 600ms ease",
          }}
        />
      </div>
      <div className="tiny" style={{ color: "var(--text-faint)", marginTop: 10, letterSpacing: "0.16em" }}>
        {totalSeconds <= 12 ? "5–12 SECONDS" : "10–20 SECONDS"}
      </div>
    </div>
  );
}

// AnalyzerErrorCard — replaces the orphan "Analyzer is busy" text.
// Shows the structured error, request id, and three action CTAs.
//
// Props:
//   error       — { type, retryable, message, request_id }
//   onRetry     — () => void
//   onPickNew   — () => void
//   feature     — string label used in the report URL ("glow-up-analyze" | "nutrition-scan")
//   testId      — root testid
export function AnalyzerErrorCard({ error, onRetry, onPickNew, pickNewLabel = "Choose another photo", pickNewIcon, feature, testId = "analyzer-error" }) {
  const type = error?.type || "processing_failure";
  const retryable = error?.retryable !== false;
  const message = error?.message || "Something went wrong. Please try again.";
  const requestId = error?.request_id || error?.requestId;

  const reportSubject = encodeURIComponent(`Nova — ${feature} failed (${type})`);
  const reportBody = encodeURIComponent(
    `Hi team,\n\nThe ${feature} analyzer failed for me.\n\n` +
      `Error type: ${type}\n` +
      `Retryable: ${retryable}\n` +
      `Request id: ${requestId || "(none)"}\n` +
      `Message: ${message}\n\n` +
      `What I was doing: \n`
  );

  return (
    <div
      data-testid={testId}
      className="card"
      style={{
        padding: 22,
        border: "1px solid rgba(255,104,104,0.32)",
        background: "rgba(255,104,104,0.04)",
        marginTop: 16,
      }}
    >
      <div
        className="tile-icon"
        style={{
          width: 44,
          height: 44,
          marginBottom: 14,
          background: "rgba(255,104,104,0.12)",
          borderColor: "rgba(255,104,104,0.32)",
        }}
      >
        <Bug size={18} color="var(--danger)" strokeWidth={1.8} />
      </div>

      <div className="h3" style={{ color: "var(--text)", marginBottom: 6 }}>
        Couldn&apos;t finish your analysis
      </div>
      <p className="body-sm" data-testid={`${testId}-message`} style={{ color: "var(--text-dim)", marginBottom: 14 }}>
        {message}
      </p>

      <div
        className="tiny"
        style={{
          color: "var(--text-faint)",
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          marginBottom: 14,
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
        }}
        data-testid={`${testId}-meta`}
      >
        <span>TYPE · {type.replace(/_/g, " ")}</span>
        {requestId && <span style={{ opacity: 0.8 }}>ID · {String(requestId).slice(0, 8)}</span>}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {retryable && onRetry && (
          <button
            type="button"
            className="btn btn-gold"
            onClick={onRetry}
            data-testid={`${testId}-retry`}
            style={{ justifyContent: "center" }}
          >
            <RotateCw size={14} /> Try again
          </button>
        )}
        {onPickNew && (
          <button
            type="button"
            className="btn btn-outline"
            onClick={onPickNew}
            data-testid={`${testId}-pick-new`}
            style={{ justifyContent: "center" }}
          >
            {pickNewIcon || <ImagePlus size={14} />} {pickNewLabel}
          </button>
        )}
        <a
          className="btn btn-ghost"
          href={`mailto:hi@glowup.app?subject=${reportSubject}&body=${reportBody}`}
          data-testid={`${testId}-report`}
          style={{ justifyContent: "center" }}
        >
          <Bug size={14} /> Report problem
        </a>
      </div>
    </div>
  );
}

// Parse the structured error envelope returned by the backend resilience helper.
// Falls back to plain text if the envelope is missing (older endpoints).
export function parseAnalyzerError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object" && detail.error) {
    return {
      type: detail.error.type,
      retryable: detail.error.retryable !== false,
      message: detail.error.message,
      request_id: detail.request_id,
      reset_at: detail.reset_at || null,
      feature: detail.feature || null,
      window_hours: detail.window_hours || null,
    };
  }
  if (typeof detail === "string") {
    return { type: "processing_failure", retryable: true, message: detail };
  }
  return {
    type: "processing_failure",
    retryable: true,
    message: err?.message || "Something went wrong. Please try again.",
  };
}
